import type { PartialMaybe, Timestamped } from '../abstract/_';
import { AbstractCollection } from '../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../abstract/Document';
import type { IPlace, PlaceID, PlaceType, PlaceTypeA } from './Places.types';
import { Addons } from './Places/Addons';
import { Facets } from './Places/Facets';
import { Faqs } from './Places/Faqs';
import { Widgets } from './Places/Widgets';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { AddressType, Language, ReverseGeocodeResponseData } from '@googlemaps/google-maps-services-js';
import type { protos } from '@googlemaps/places';
import { GeoPoint, limit, type QueryConstraint, where } from 'firebase/firestore';

@Identifiable
@Timestampable
@Untrackable
export class Place extends AbstractDocument<IPlace> {
  readonly collections = {
    Addons: new Addons(this),
    Facets: new Facets(this),
    Faqs: new Faqs(this),
    Widgets: new Widgets(this),
  };

  /**
   * Returns the place approximate radius (in km) based off the viewport bounding box approximate area.
   *
   * @param snapshot The `Place` snapshot, optional.
   */
  async getApproximateRadius(snapshot?: IPlace) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (snapshot?.viewport?.area == null) {
      throw new Error(`Unable to calculate approximate radius for place '${this.id}'.`);
    }

    /**
     * The bounding box is likely rectangular, but we assume it's a square with side length yielding the same area.
     * Then we can use that side length to calculate the radius of a circle that could be contained within that square.
     */
    return Math.sqrt(snapshot.viewport.area) / 2;
  }

  /**
   * Returns the most language-specific name available for the given place.
   *
   * @param language The language code to use to localize locations, optional.
   * @param document The hydrated document, optional.
   */
  async getPlaceName(language?: Language, document?: Partial<IPlace>) {
    if (document == null) {
      document = await this.get(true);
    }

    if (language == null) {
      language = Places.translations[document.country!];
    }

    return document._translations?.[language!]?.alias ?? document._translations?.[language!]?.name ?? document.alias ?? document.name;
  }

  /**
   * Get the place *coloquial* type.
   * This is a helper method to aid with Bridebook-specific terminology, specifically for search.
   *
   * @todo We don't yet express `region` type clearly in this collection.
   * @param snapshot The `Place` snapshot, optional.
   */
  async getPlaceType(snapshot?: IPlace) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (snapshot?.types?.length > 0) {
      if (snapshot.types.includes('country') === true) {
        return 'country';
      }

      const county =
        snapshot.country === CountryCodes.GB
          ? 'county'
          : gazetteer
              .getMarketByCountry(snapshot.country)
              .sortAdminAreas(['administrative_area_level_2', 'administrative_area_level_1'] as PlaceType[], 1)
              .shift()!;

      if (snapshot.types.includes(county) === true) {
        return 'county';
      }

      if (snapshot.types.includes('custom') === true) {
        return 'custom';
      }

      if (snapshot.types.includes('locality') === true || snapshot.types.includes('postal_town') === true) {
        return 'town';
      }
    }

    return null;
  }
}

export class Places extends AbstractCollection<Place, IPlace> {
  static path = 'places';
  static translations: Partial<Record<CountryCodes, Language>> = {
    [CountryCodes.AT]: 'de' as Language,
    [CountryCodes.CH]: 'de' as Language,
    [CountryCodes.DE]: 'de' as Language,
    [CountryCodes.FR]: 'fr' as Language,
  };

  constructor() {
    super(Places.path, Place);
  }

  static get _() {
    return new this();
  }

  /**
   * Returns the approximate area of the viewport bounding box, in square kilometers.
   */
  static area(bounds: IPlace['viewport']['bounds']) {
    const degToRad = (value: number) => value * 0.017453292519943295;
    const latitudes = [bounds[0].latitude, bounds[1].latitude].map(degToRad);
    const longitude = {
      delta: degToRad(bounds[1].longitude - bounds[0].longitude),
    };

    if (longitude.delta < -Math.PI) {
      longitude.delta += 2 * Math.PI;
    }

    if (longitude.delta > Math.PI) {
      longitude.delta -= 2 * Math.PI;
    }

    return Math.abs(6_371 ** 2 * longitude.delta * (Math.sin(latitudes[0]) - Math.sin(latitudes[1])));
  }

  /**
   * Returns the geometric centroid of the viewport bounding box.
   */
  static centroid(bounds: IPlace['viewport']['bounds']) {
    return new GeoPoint((bounds[0].latitude + bounds[1].latitude) / 2, (bounds[0].longitude + bounds[1].longitude) / 2);
  }

  /**
   * Returns a new custom place derived from multiple Place descendants.
   *
   * @param ids Descendant Place IDs.
   * @param name The new place name.
   */
  async combine(ids: PlaceID[], name: string): Promise<Omit<IPlace, keyof Timestamped>> {
    const places = await Promise.all(ids.map((id) => Places._.getById(id).get()));
    const ancestors = places
      .map((place) => place.ancestors)
      .reduce((common, ancestors) => common.filter((a) => ancestors.some((b) => a === b)));

    if (ancestors.length === 0) {
      throw new Error(`Unable to combine places that don't share any common ancestors.`);
    }

    const components = places
      .map((place) => place.components)
      .reduce((common, components) => common.filter((a) => components.some((b) => a.id === b.id)));

    /**
     * Generate a new Place ID based on the immediate common ancestor ID and a suffixed slug.
     */
    const id: PlaceID = [
      ancestors[ancestors.length - 1],
      name
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[\s,]+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/[-]{2,}/g, '-'),
    ].join('|');

    const { n, s, w, e } = places
      .flatMap((place) => place.viewport.bounds)
      .reduce(
        (acc, bound) => ({
          n: Math.max(acc.n, bound.latitude),
          s: Math.min(acc.s, bound.latitude),
          e: Math.max(acc.e, bound.longitude),
          w: Math.min(acc.w, bound.longitude),
        }),
        {
          n: -Infinity,
          s: Infinity,
          e: -Infinity,
          w: Infinity,
        },
      );

    const bounds: IPlace['viewport']['bounds'] = [new GeoPoint(n, w), new GeoPoint(s, e)];
    const result: Omit<IPlace, keyof Timestamped> = {
      ancestors,
      components: [
        {
          id,
          name: {
            long: name,
            short: name,
          },
          types: ['custom'],
        },
        ...components,
      ],
      coordinates: Places.centroid(bounds),
      country: (components.find((value) => value.types.includes('country'))?.name?.short ?? '*') as CountryCodes,
      descendants: ids.sort(),
      id,
      name,
      slug: name,
      types: ['custom'],
      viewport: {
        area: Places.area(bounds),
        bounds,
      },
    };

    return result;
  }

  /**
   * Returns an `IPlace` from the geocoding of a political/administrative location.
   *
   * @param query The string to search for.
   * @param type Google Place Typ (A), optional.
   */
  static async geocode(query: string, type?: PlaceTypeA): Promise<Omit<IPlace, keyof Timestamped>> {
    const payload: protos.google.maps.places.v1.ISearchTextRequest = {
      languageCode: 'en' as Language,
      textQuery: query,
    };

    if (type != null) {
      payload.includedType = type;
      payload.strictTypeFiltering = true;
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': process.env.GEO_CODE_KEY as string,
      /**
       * Subset of relevant (Location Only SKU) fields to return.
       *
       * @see https://developers.google.com/maps/documentation/places/web-service/text-search#fieldmask
       */
      'X-Goog-FieldMask': ['places.addressComponents', 'places.id', 'places.location', 'places.types', 'places.viewport'].join(','),
    };

    const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
      body: JSON.stringify(payload),
      headers,
      method: 'POST',
    });

    if (response.ok !== true) {
      throw new Error(response.statusText);
    }

    const { places }: protos.google.maps.places.v1.ISearchTextResponse = await response.json();

    if (places == null || Array.isArray(places) !== true) {
      throw new Error(`No places found for '${query}' (${type}).`);
    }

    const place = places.find((place) => {
      if (type != null) {
        return place.types!.includes(type);
      }

      return ['political'].some((type) => place.types!.includes(type));
    });

    if (place == null) {
      throw new Error(`No suitable place found for '${query}' (${type}).`);
    }

    const viewport: IPlace['viewport'] = {
      area: 0,
      bounds: [
        new GeoPoint(place.viewport!.high!.latitude!, place.viewport!.high!.longitude!),
        new GeoPoint(place.viewport!.low!.latitude!, place.viewport!.low!.longitude!),
      ],
    };

    const result: Omit<IPlace, keyof Timestamped> = {
      _translations: {},
      ancestors: [],
      components: place.addressComponents!.map((component, index) => ({
        id: index > 0 ? '' : place.id!,
        name: {
          long: component.longText!,
          short: component.shortText!,
        },
        types: component.types as PlaceType[],
      })),
      coordinates: new GeoPoint(place.location!.latitude!, place.location!.longitude!),
      country: (place.addressComponents!.find((value) => value.types!.includes('country'))?.shortText ?? '*') as CountryCodes,
      id: place.id!,
      name: place.addressComponents![0].longText!,
      slug: '',
      types: place.types as PlaceType[],
      viewport: {
        ...viewport,
        area: Places.area(viewport.bounds),
      },
    };

    const ancestors = await Places.geocodeAncestors(result);

    if (ancestors.length > 0) {
      result.ancestors = ancestors.map((ancestor) => ancestor.id);
      result.components = [result.components.shift()!, ...ancestors.pop()!.components];
    }

    const language = Places.translations[result.country];

    /**
     * Fetch place translations for certain countries.
     */
    if (language != null) {
      result._translations = await Places.translate(result, language);
    }

    /**
     * Populate canonical slug with the most relevant place name.
     */
    if (result.slug === '') {
      result.slug = result._translations?.[language!]?.alias ?? result._translations?.[language!]?.name ?? result.alias ?? result.name;
    }

    return result;
  }

  /**
   * Reverse geocodes the centroid of a `IPlace` in order to return relevant geographical `IPlace` ancestors.
   * Uses the Geocoding API instead of the Places API, since the latter is not always reliable.
   * Returns an array of relevant `IPlace` ancestors, from least to most specific.
   *
   * @param place The `IPlace`.
   */
  static async geocodeAncestors(place: PartialMaybe<IPlace, Timestamped>): Promise<Omit<IPlace, keyof Timestamped>[]> {
    /**
     * We can't reverse geocode if we don't have the centroid.
     */
    if (place.coordinates == null) {
      return [];
    }

    /**
     * Extract relevant (and supported) administrative/political place ancestor types.
     *
     * @see https://developers.google.com/maps/documentation/geocoding/requests-reverse-geocoding#optional_parameters
     */
    const ancestors: AddressType[] = [];

    for (const component of place.components.slice(1)) {
      const type = component.types.find((type) => /^(?:administrative_area_level_[0-9]+|country|locality)$/.test(type));

      if (type != null) {
        ancestors.push(type as AddressType);
      }
    }

    /**
     * There aren't any relevant ancestors.
     * Likely we are already dealing with a country and shouldn't try to reverse-geocode it's centroid.
     */
    if (ancestors.length === 0) {
      return [];
    }

    const query = new URLSearchParams({
      key: process.env.GEO_CODE_KEY as string,
      language: 'en' as Language,
      latlng: [place.coordinates.latitude, place.coordinates.longitude].map((value) => value.toFixed(5)).join(','),
      result_type: ancestors.join('|'),
    });

    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${query.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (response.ok !== true) {
      throw new Error(response.statusText);
    }

    const data: ReverseGeocodeResponseData = await response.json();

    if (data.status !== 'OK') {
      throw new Error(data.status);
    }

    const language = Places.translations[place.country];
    const translations: IPlace['_translations'][] = [];

    /**
     * Fetch place translations for certain countries for the different address components.
     *
     * It's more efficient to just issue a second localized reverse-geocoding request, rather than a place detail request per component.
     */
    if (language != null) {
      if (query.get('language') !== language) {
        query.set('language', language);
      }

      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${query.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      });

      if (response.ok !== true) {
        throw new Error(response.statusText);
      }

      const data: ReverseGeocodeResponseData = await response.json();

      if (data.status !== 'OK') {
        throw new Error(data.status);
      }

      const components = data.results?.shift()?.address_components ?? [];

      for (const component of components) {
        translations.push({
          [language]: {
            name: component.long_name,
          },
        });
      }
    }

    return data.results
      .map((place, index) => {
        const ancestors = data.results
          .slice(index + 1)
          .map((value) => value.place_id)
          .reverse();

        const viewport: IPlace['viewport'] = {
          area: 0,
          bounds: [
            new GeoPoint(place.geometry.viewport.northeast.lat, place.geometry.viewport.northeast.lng),
            new GeoPoint(place.geometry.viewport.southwest.lat, place.geometry.viewport.southwest.lng),
          ],
        };

        const result: Omit<IPlace, keyof Timestamped> = {
          _translations: translations.shift() ?? {},
          ancestors,
          components: place.address_components.map((component, index) => ({
            id: index > 0 ? ancestors.pop()! : place.place_id,
            name: {
              long: component.long_name,
              short: component.short_name,
            },
            types: component.types as PlaceType[],
          })),
          coordinates: new GeoPoint(place.geometry.location.lat, place.geometry.location.lng),
          country: place.address_components.find((value) => value.types.includes('country' as AddressType))?.short_name as CountryCodes,
          id: place.place_id,
          name: place.address_components[0].long_name,
          slug: '',
          types: place.types as PlaceType[],
          viewport: {
            ...viewport,
            area: Places.area(viewport.bounds),
          },
        };

        /**
         * Populate canonical slug with the most relevant place name.
         */
        if (result.slug === '') {
          result.slug = result._translations?.[language!]?.alias ?? result._translations?.[language!]?.name ?? result.alias ?? result.name;
        }

        return result;
      })
      .reverse();
  }

  /**
   * Returns translations for the provided `IPlace`.
   *
   * @param place The `IPlace`.
   * @param language The language code to use to localize the place.
   */
  static async translate(place: PartialMaybe<IPlace, Timestamped>, language: Language): Promise<IPlace['_translations']> {
    const result: IPlace['_translations'] = {};

    /**
     * We are unable to fetch place name translations for custom places.
     */
    if (place.id.includes('|') === true) {
      return result;
    }

    const query = new URLSearchParams({
      fields: 'addressComponents',
      languageCode: language,
    });

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': process.env.GEO_CODE_KEY as string,
    };

    const response = await fetch(`https://places.googleapis.com/v1/places/${place.id}?${query.toString()}`, {
      headers,
      method: 'GET',
    });

    if (response.ok !== true) {
      throw new Error(response.statusText);
    }

    const {
      addressComponents: [component],
    }: protos.google.maps.places.v1.Place = await response.json();

    if (result[component.languageCode!] == null) {
      result[component.languageCode!] = {};
    }

    if (result[component.languageCode!].name == null) {
      result[component.languageCode!].name = component.longText!;
    }

    return result;
  }

  /**
   * Queries a place by slug.
   *
   * @param slug The slug to search for.
   * @param country The country code, optional.
   */
  getBySlug(slug: IPlace['slug'], country?: CountryCodes) {
    const constraints: QueryConstraint[] = [where('slug', '==', slug), limit(1)];

    if (country != null) {
      constraints.unshift(where('country', '==', country));
    }

    return this.query(constraints);
  }

  /**
   * Returns places that classify as type for a given country.
   *
   * @param country The country code.
   * @param type - possible Google place type
   * @param slug Optional place slug to search for.
   */
  getPlaces(country: CountryCodes, type: IPlace['types'][number], slug?: IPlace['slug']) {
    const constraints: QueryConstraint[] = [where('country', '==', country)];

    constraints.push(where('types', 'array-contains', type));

    if (slug != null) {
      constraints.push(where('slug', '==', slug));
    }

    return this.query(constraints);
  }

  /**
   * Returns places that classify as "counties" for a given country.
   *
   * @param country The country code.
   * @param slug Optional place slug to search for.
   */
  getCounties(country: CountryCodes, slug?: IPlace['slug']) {
    const constraints: QueryConstraint[] = [where('country', '==', country)];

    if (country === CountryCodes.GB) {
      /**
       * England is an exception as `administrative_area_level_2` places represent administrative counties.
       * However, in some cases we are interested in "ceremonial" counties.
       * Therefore, we manually specify the custom `county` type on relevant places and query for that.
       *
       * @see https://en.wikipedia.org/wiki/Ceremonial_counties_of_England
       */
      constraints.push(where('types', 'array-contains', 'county'));
    } else {
      /**
       * Remap from our common Address struct back to Google Place types using Gazetteer.
       *
       * @see packages/toolbox/src/search-suppliers/elastic-queries/utils.ts
       * @see packages/web/app-shared/lib/search/utils/parse-google-place.ts
       */
      const type = gazetteer
        .getMarketByCountry(country)
        .sortAdminAreas(['administrative_area_level_2', 'administrative_area_level_1'], 1)
        .shift();

      constraints.push(where('types', 'array-contains', type));
    }

    if (slug != null) {
      constraints.push(where('slug', '==', slug));
    }

    return this.query(constraints);
  }

  /**
   * Returns a `Place` instance for a county matching slug within the given country.
   * Throws if no county can be found.
   *
   * @param country The country code.
   * @param slug The slug to search for.
   */
  async getCounty(country: CountryCodes, slug: IPlace['slug']) {
    const places = Object.values(await this.getCounties(country, slug).get(false));

    if (places.length === 0) {
      throw new Error(`Unable to find county '${slug}' in ${country}.`);
    }

    return this.getById(places.shift()!.snapshot.id);
  }

  /**
   * Returns a `Place` instance for a specific country.
   * Throws if the country cannot be found.
   *
   * @param country The country code.
   */
  async getCountry(country: CountryCodes) {
    const places = Object.values(
      await this.query([where('country', '==', country), where('types', 'array-contains', 'country')]).get(false),
    );

    if (places.length === 0) {
      throw new Error(`Unable to find country ${country}.`);
    }

    return this.getById(places.shift()!.snapshot.id).get();
  }
}
